import { Button, MenuItem } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import { FuelLayer } from '@/domain/protocols/Fuel'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'

import { ToolContainer } from '../ToolbarTools.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { FuelFormStateMutator } from '@/presentation/modules/MapScreen/states/FuelFormState'
import { FuelState, FuelStateMutator, FuelStateProps } from '@/presentation/modules/MapScreen/states/FuelState'
import {
  MapADHPState,
  MapADHPStateMutator,
  MapADHPStateProps
} from '@/presentation/modules/MapScreen/states/MapADHPState'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

type FuelLayerOptions = {
  key: FuelLayer
  label: string
  isActive: boolean
}

function FuelTool() {
  const { t } = useI18n()

  const fuelState = useBehaviorSubject<FuelStateProps>(FuelState)
  const mapADHPState = useBehaviorSubject<MapADHPStateProps>(MapADHPState)
  const [_, mapLayersStateW] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)

  const FuelLayerOptions: FuelLayerOptions[] = [
    {
      key: 'avgas',
      label: t('TOOLBAR_FUEL_ITEM_AVGAS'),
      isActive: fuelState.layer === 'avgas'
    },
    {
      key: 'jet-a',
      label: t('TOOLBAR_FUEL_ITEM_JET-A'),
      isActive: fuelState.layer === 'jet-a'
    }
  ]

  const handleFuelLayerChange = (layer: FuelLayer) => {
    const currentFuelLayer = fuelState.layer
    const newLayer = currentFuelLayer === layer ? null : layer

    if (newLayer && !currentFuelLayer) {
      FuelStateMutator.setPreviousADHPState({
        aerodromes: mapADHPState.aerodromes,
        helipads: mapADHPState.helipads,
        userWaypoints: mapADHPState.userWaypoints
      })
      mapLayersStateW.setAerodromes(false)
      mapLayersStateW.setHelipads(false)
      mapLayersStateW.setUserWaypoints(false)
      MapADHPStateMutator.setAerodromes(false)
      MapADHPStateMutator.setHelipads(false)
      MapADHPStateMutator.setUserWaypoints(false)
    } else if (!newLayer && currentFuelLayer) {
      if (fuelState.previousADHPState) {
        MapADHPStateMutator.setAerodromes(fuelState.previousADHPState.aerodromes)
        MapADHPStateMutator.setHelipads(fuelState.previousADHPState.helipads)
        MapADHPStateMutator.setUserWaypoints(fuelState.previousADHPState.userWaypoints)
        mapLayersStateW.setAerodromes(fuelState.previousADHPState.aerodromes)
        mapLayersStateW.setHelipads(fuelState.previousADHPState.helipads)
        mapLayersStateW.setUserWaypoints(fuelState.previousADHPState.userWaypoints)
      }

      FuelStateMutator.setPreviousADHPState(null)
    }

    FuelStateMutator.setLayer(newLayer)
  }

  const handleRegisterSupplier = () => {
    // Pela toolbar não há aeródromo selecionado; o tipo de combustível só existe se houver camada ativa.
    sendMetrics(MetricsEvents.FUEL_REGISTER_PROVIDER_CLICKED, {
      origin: 'fuel-tool',
      ...(fuelState.layer ? { fuelType: fuelState.layer } : {})
    })

    FuelFormStateMutator.showRegisterProviderFromTool()
  }

  return (
    <ToolContainer gap={12}>
      {FuelLayerOptions.map((element) => (
        <MenuItem
          key={element.key}
          title={element.label}
          size="sm"
          hasSwitch
          isSwitchChecked={element.isActive}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_FUEL_TYPE_SELECTED, {
              extra: element.key,
              active: !element.isActive
            })
            handleFuelLayerChange(element.key)
          }}
        />
      ))}
      <Button
        label={t('TOOLBAR_FUEL_ITEM_REGISTER-SUPPLIER')}
        variant="outline"
        color="secondary"
        size="xs"
        leftIcon="Plus"
        fullWidth
        onPress={handleRegisterSupplier}
      />
    </ToolContainer>
  )
}

export { FuelTool }
